import Header from "../components/Header"

import Styles from "./depression-test-phq-9.module.scss"
export { getServerSideProps } from "../utils/getServerSideProps"
import { useState, useCallback, useMemo } from "react"
import Footer from "../components/local/Footer"

import { useTranslation } from "next-i18next"
import FormInput from "../components/FormInput"

import ProgressSteps from "../components/ProgressSteps"
import Image from "next/image"
import Button from "../components/Button"
import TalkToSolace from "../components/TalkToSolace"

import { isMobile } from "../utils/browser"

function StressTestPage() {
  return (
    <>
      <Header />
      <main className={Styles.Wrapper} id="page-wrap">
        <StressTest />
      </main>
      <Footer />
    </>
  )
}

function StressTest() {
  const { t } = useTranslation("tests", "common")

  const [values, setValues] = useState({})

  const getValue = (key) => values[key] || 0

  const setValue = (key, value) => {
    setValues({ ...values, [key]: value })
  }

  const [step, setStep] = useState(0)

  const stepNames = [
    t("TEST_STRESS_PSS_PERCEPTION"),
    t("TEST_STRESS_PSS_EMOTIONAL"),
    t("TEST_STRESS_PSS_OPTIMISM"),
    t("TEST_STRESS_PSS_RESULTS"),
  ]

  const testOptions = [
    {
      value: 0,
      label: t("TEST_ADHD_ASRSV1_NEVER"),
    },
    {
      value: 1,
      label: t("TEST_ADHD_ASRSV1_RARELY"),
    },
    {
      value: 2,
      label: t("TEST_ADHD_ASRSV1_SOMETIMES"),
    },
    {
      value: 3,
      label: t("TEST_ADHD_ASRSV1_OFTEN"),
    },
    {
      value: 4,
      label: t("TEST_ADHD_ASRSV1_VERY_OFTEN"),
    },
  ]

  const handleValueChange = (key) => (e) => {
    setValue(key, e.target.value)
  }

  const scrollUp = () => {
    if (isMobile()) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        })
      }, 50)
    }
  }

  const handleBack = () => {
    setStep(step - 1)
    scrollUp()
  }

  const handleMainButton = useCallback(async () => {
    scrollUp()

    setStep(step + 1)
  }, [step])

  const PERCEPTION_QUESTIONS = [
    {
      label: t("TEST_STRESS_PSS_UPSET_UNEXPECTEDLY_LABEL"),
      id: "TEST_STRESS_PSS_UPSET_UNEXPECTEDLY",
    },
    {
      label: t("TEST_STRESS_PSS_FELT_UNABLE_CONTROL_LABEL"),
      id: "TEST_STRESS_PSS_FELT_UNABLE_CONTROL",
    },
    {
      label: t("TEST_STRESS_PSS_DEALT_WITH_IRRITATIONS_LABEL"),
      id: "TEST_STRESS_PSS_DEALT_WITH_IRRITATIONS",
    },
    {
      label: t("TEST_STRESS_PSS_CONTROL_IRRITATIONS_LABEL"),
      id: "TEST_STRESS_PSS_CONTROL_IRRITATIONS",
    },
    {
      label: t("TEST_STRESS_PSS_FELT_ON_TOP_LABEL"),
      id: "TEST_STRESS_PSS_FELT_ON_TOP",
    },
    {
      label: t("TEST_STRESS_PSS_ANGERED_LACK_CONTROL_LABEL"),
      id: "TEST_STRESS_PSS_ANGERED_LACK_CONTROL",
    },
  ]

  const EMOTIONAL_QUESTIONS = [
    {
      label: t("TEST_STRESS_PSS_FELT_NERVOUS_STRESSED_LABEL"),
      id: "TEST_STRESS_PSS_FELT_NERVOUS_STRESSED",
    },
    {
      label: t("TEST_STRESS_PSS_DIFFICULTIES_PILING_UP_LABEL"),
      id: "TEST_STRESS_PSS_DIFFICULTIES_PILING_UP",
    },
  ]

  const OPTIMISM_QUESTIONS = [
    {
      label: t("TEST_STRESS_PSS_CONFIDENT_PERSONAL_PROBLEMS_LABEL"),
      id: "TEST_STRESS_PSS_CONFIDENT_PERSONAL_PROBLEMS",
    },
    {
      label: t("TEST_STRESS_PSS_FELT_THINGS_GOING_WAY_LABEL"),
      id: "TEST_STRESS_PSS_FELT_THINGS_GOING_WAY",
    },
  ]

  const QUESTIONS = [
    PERCEPTION_QUESTIONS,
    EMOTIONAL_QUESTIONS,
    OPTIMISM_QUESTIONS,
  ]

  const stepFields = (step) => (
    <>
      {step < 3 ? (
        QUESTIONS[step].map(({ label, id }) => (
          <FormInput
            key={id}
            label={label}
            type="select-boxes"
            options={testOptions}
            value={getValue(id)}
            onChange={handleValueChange(id)}
          />
        ))
      ) : (
        <TestResult values={values} />
      )}
    </>
  )

  const mainButtonLabel = useMemo(() => {
    const labelNext = (
      <div className="flex items-center">
        {t("HOME_NEXT", { ns: "common" })}
        <Image
          width={24}
          height={24}
          className="ml-2"
          src="/assets/icon-arrow-right-white.svg"
          alt="arrow-right"
        />
      </div>
    )

    const stepLabels = [labelNext, labelNext, t("TEST_SEE_YOUR_RESULTS")]

    return stepLabels[step]
  }, [t, step])

  return (
    <>
      <ProgressSteps step={step} stepNames={stepNames} />
      {stepFields(step)}
      <div className="flex justify-between w-full h-20 relative">
        {[1, 2, 3].includes(step) && (
          <Button onClick={handleBack} style="secondary">
            <div className="flex items-center">
              <Image
                width={24}
                height={24}
                className="mr-2"
                src="/assets/icon-arrow-left-black.svg"
                alt="arrow-right"
              />

              {t("HOME_BACK", { ns: "common" })}
            </div>
          </Button>
        )}

        {step !== 3 ? (
          <Button
            onClick={handleMainButton}
            style="primary"
            className="right-0 absolute text-lg sm:text-2xl"
          >
            {mainButtonLabel}
          </Button>
        ) : (
          <TalkToSolace />
        )}
      </div>
    </>
  )
}

const importantKeys = []

const calcScore = (values) =>
  Object.entries(values).reduce(
    (acc, [key, value]) =>
      acc + (importantKeys.includes(key) ? 2 * value : value),
    0
  )

function TestResult({ values }) {
  const score = calcScore(values)
  const { t } = useTranslation("tests")

  const getResults = (score) => {
    if (score <= 4) {
      return {
        title: t("TEST_STRESS_PSS_VERY_LOW"),
        description: t("TEST_STRESS_PSS_VERY_LOW_DESCRIPTION"),
      }
    }

    if (score <= 9) {
      return {
        title: t("TEST_STRESS_PSS_LOW"),
        description: t("TEST_STRESS_PSS_LOW_DESCRIPTION"),
      }
    }

    if (score <= 14) {
      return {
        title: t("TEST_STRESS_PSS_MODERATE"),
        description: t("TEST_STRESS_PSS_MODERATE_DESCRIPTION"),
      }
    }

    if (score <= 19) {
      return {
        title: t("TEST_STRESS_PSS_HIGH"),
        description: t("TEST_STRESS_PSS_HIGH_DESCRIPTION"),
      }
    }

    if (score <= 24) {
      return {
        title: t("TEST_STRESS_PSS_VERY_HIGH"),
        description: t("TEST_STRESS_PSS_VERY_HIGH_DESCRIPTION"),
      }
    }

    return {
      title: t("TEST_STRESS_PSS_EXTREMELY_HIGH"),
      description: t("TEST_STRESS_PSS_EXTREMELY_HIGH_DESCRIPTION"),
    }
  }

  const { title, description } = getResults(score)

  return (
    <>
      <h2>{title}</h2>
      <h3>{description}</h3>
    </>
  )
}

export default StressTestPage
